import { useEffect, useState } from 'react';
import { Plus, Search, Phone, Mail, Calendar, FileText, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useActivityStore } from '@/store/activityStore';
import { formatDateTime } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { ActivityFormDialog } from '@/components/activities/ActivityFormDialog';
import type { ActivityType } from '@/types/crm';

const activityIcons: Record<ActivityType, React.ReactNode> = {
  call: <Phone className="h-4 w-4" />,
  meeting: <Calendar className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  task: <CheckCircle className="h-4 w-4" />,
  note: <FileText className="h-4 w-4" />,
};

export function ActivitiesPage() {
  const { activities, isLoading, filters, fetchActivities, setFilters, completeActivity } = useActivityStore();
  const { toast } = useToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities, filters]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setFilters({ ...filters, search: value });
  };

  const handleTypeFilter = (type: string) => {
    setFilters({ ...filters, type: type === 'all' ? undefined : type as ActivityType });
  };

  const handleComplete = async (id: string) => {
    try {
      await completeActivity(id);
      toast({ title: 'Activity completed' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Activities</h1>
          <p className="text-muted-foreground">Track calls, meetings, emails, and tasks</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Activity
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search activities..." value={search} onChange={(e) => handleSearch(e.target.value)} className="pl-9" />
        </div>
        <Select onValueChange={handleTypeFilter} defaultValue="all">
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="call">Calls</SelectItem>
            <SelectItem value="meeting">Meetings</SelectItem>
            <SelectItem value="email">Emails</SelectItem>
            <SelectItem value="task">Tasks</SelectItem>
            <SelectItem value="note">Notes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : activities.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No activities found</p>
        ) : (
          activities.map((activity) => (
            <Card key={activity.id} className={activity.is_completed ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${activity.is_completed ? 'bg-green-100 text-green-600' : 'bg-primary/10 text-primary'}`}>
                    {activityIcons[activity.type as ActivityType]}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium ${activity.is_completed ? 'line-through' : ''}`}>{activity.subject}</h3>
                      <Badge variant="secondary" className="capitalize">{activity.type}</Badge>
                      {activity.is_completed && <Badge className="bg-green-100 text-green-800" variant="secondary">Completed</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{activity.description || 'No description'}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {activity.company_name && <span>Company: {activity.company_name}</span>}
                      {activity.deal_title && <span>Deal: {activity.deal_title}</span>}
                      {activity.due_date && <span>Due: {formatDateTime(activity.due_date)}</span>}
                    </div>
                  </div>
                  {!activity.is_completed && activity.type === 'task' && (
                    <Button size="sm" variant="outline" onClick={() => handleComplete(activity.id)}>
                      Complete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <ActivityFormDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
    </div>
  );
}
